'use client';

import type { ComponentType } from 'react';
import { useEffect, useState } from 'react';

/**
 * On mobile (Lighthouse viewport + most real-world traffic on this page) the
 * desktop header is CSS-hidden, but the JS still ships because the import is
 * static. Use a viewport-aware dynamic import so anonymous mobile viewers
 * never download the AppHeader chunk — and with it the HeaderAuthSection
 * → AuthenticatedActions → notification-config dep chain that Lighthouse
 * was flagging as ~23 KiB of unused JavaScript.
 *
 * Mobile viewers see no header here (the page has its own back overlay via
 * MobileLayout); desktop viewers get the full AppHeader after first render.
 *
 * A `matchMedia('change')` listener also picks up the case where a viewer
 * starts on a mobile-width window (header not loaded) and resizes up past
 * 768px — without this, the header would stay missing until next navigation.
 */
const DESKTOP_QUERY = '(min-width: 768px)';

export function ActivityPageHeader() {
  const [AppHeader, setAppHeader] = useState<ComponentType<Record<string, never>> | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let cancelled = false;
    const loadAppHeader = () => {
      if (cancelled || AppHeader) return;
      void import(/* webpackPrefetch: false, webpackPreload: false */ '@website/components/AppHeader/AppHeader').then(
        (m) => {
          if (!cancelled) setAppHeader(() => m.default);
        }
      );
    };

    const mql = window.matchMedia(DESKTOP_QUERY);
    if (mql.matches) {
      loadAppHeader();
      return () => {
        cancelled = true;
      };
    }

    // Mobile on mount — wait for a resize up to desktop before fetching the chunk.
    const onChange = (e: MediaQueryListEvent) => {
      if (e.matches) loadAppHeader();
    };
    mql.addEventListener('change', onChange);
    return () => {
      cancelled = true;
      mql.removeEventListener('change', onChange);
    };
  }, [AppHeader]);

  if (!AppHeader) return null;

  return (
    <div className="hidden md:block sticky top-0 z-100">
      <AppHeader />
    </div>
  );
}
